import * as nameConstants from "../constants/nameConstants";
import {wallMeshBBoxName} from "../constants/nameConstants";
import {Vector3} from "three";
import {buildingGroup} from "../main";
import {findChildByName, findChildrenByName} from "../utils/threeUtils";
import {t} from "../../i18n";

const saveCSVUrl = "/configurator/save-parts-list";
const woodThickness = 440;
export function exportPartsList(download) {
    let rows = [];
    let wallMeshes = [];
    let wallGroups = findChildrenByName(buildingGroup, 'wallGroup');
    wallGroups.forEach((group) => {
        wallMeshes.push(...findChildrenByName(group, 'wallMesh', true));
    });
    getWallMeshElementRows(rows, "Wandelement", wallMeshes);

    let raehmMeshes = findChildrenByName(buildingGroup, nameConstants.raehmMeshName, true, true);
    rows = getFixedSizeElementRows(rows, "Rähm", raehmMeshes);

    let schwelleMeshes = findChildrenByName(buildingGroup, nameConstants.schwelleMeshName, true, true);
    rows = getFixedSizeElementRows(rows, "Schwelle", schwelleMeshes);

    let pillarMeshes = findChildrenByName(buildingGroup, nameConstants.pillarMeshName, true, true);
    rows = getFixedSizeElementRows(rows, "Stütze", pillarMeshes);

    let beamMeshes = findChildrenByName(buildingGroup, nameConstants.supportBeamMeshName, true, true);
    rows = getFixedSizeElementRows(rows, "Unterzug", beamMeshes);

    let bruestungsRiegelMesh = findChildrenByName(buildingGroup, "querbalkenMesh", true, true);
    rows = getFixedSizeElementRows(rows, "Brüstungsriegel", bruestungsRiegelMesh);

    let sturzRiegelMesh = findChildrenByName(buildingGroup, "sturzRiegel", true, true);
    rows = getFixedSizeElementRows(rows, "Sturzriegel", sturzRiegelMesh);

    let roof = findChildByName(buildingGroup, nameConstants.roofGroupName);
    if (roof !== null) {
        let roofMeshes = findChildrenByName(roof, nameConstants.wallMeshName, true, true);
        getWallMeshElementRows(rows, "Deckenelement", roofMeshes);

        let attikaMeshes = findChildrenByName(roof, nameConstants.attikaMeshName, true, true);
        rows = getFixedSizeElementRows(rows, "Attika", attikaMeshes);
    }

    let floor = findChildByName(buildingGroup, nameConstants.floorGroupName);
    if (floor !== null) {
        let floorMeshes = findChildrenByName(floor, nameConstants.wallMeshName, true, true);
        getWallMeshElementRows(rows, "Bodenelement", floorMeshes);
    }

    numberElements(rows);
    translatePartNames(rows);
    return exportCSV(rows, download);
}

function getFixedSizeElementRows(uniqueRows, type, elements) {
    let rows = [];

    elements.forEach((element) => {
        if (element.type === 'Object3D' || !element.isMesh) {
            return;
        }

        if (!hasFixedSizeUserData(element)) {
            return;
        }

        if (type === "Stütze") {
            rows.push({
                type: type,
                depth: getMillimeterMeasurement(element.userData.depth),
                width: getMillimeterMeasurement(element.userData.height),
                height: getMillimeterMeasurement(element.userData.width),
                volume: getRoundedVolume(element.userData.originalVolume)
            });
        } else {
            rows.push({
                type: type,
                depth: getMillimeterMeasurement(element.userData.depth),
                height: getMillimeterMeasurement(element.userData.height),
                width: getMillimeterMeasurement(element.userData.width),
                volume: getRoundedVolume(element.userData.originalVolume)
            });
        }
    });

    rows = filterAndCountRows(rows);
    rows.forEach((row) => {
        uniqueRows.push([
            row.type, row.depth, row.height, row.width, row.volume, 
            row.count.toString(), 
            (row.volume * row.count).toFixed(3),
            (row.volume * woodThickness).toFixed(3)
        ]);
    });

    return uniqueRows;
}

function getWallMeshElementRows(uniqueRows, type, elements) {
    let rows = [];

    elements.forEach((wallMesh) => {
        if (wallMesh.type === 'Object3D' || !wallMesh.isMesh || wallMesh.name.includes(wallMeshBBoxName)) {
            return;
        }

        wallMesh.geometry.computeBoundingBox();
        let bounds = wallMesh.geometry.boundingBox;
        let size = bounds.getSize(new Vector3());

        //Breite und Höhe sind hier anders zu betrachten (s. #11285). Die Höhe ist die Width des Elements im Konfigurator. Die Länge ist die Höhe des Elements im Konfigurator.
        //Dies resultiert daraus das die Länge in der Stückliste immer das Maß angibt welches für den Längenzuschnitt relevant ist.
        let height = getMillimeterMeasurement(size.x);
        let width = getMillimeterMeasurement(size.z);
        let depth = getMillimeterMeasurement(size.y);
        let volume = getRoundedVolume(wallMesh.userData.originalVolume);

        if (volume <= 0) {
            return;
        }

        rows.push({type: type, depth: depth, height: height, width: width, volume: volume});
    });

    rows = filterAndCountRows(rows);
    rows.forEach((row) => {
        uniqueRows.push([
            row.type, row.depth, row.height, row.width, row.volume,
            row.count.toString(),
            (row.volume * row.count).toFixed(3),
            (row.volume * woodThickness).toFixed(3)
        ]);
    });

    return uniqueRows;
}

function filterAndCountRows(entries) {
    // Create an object to store unique entries and their counts
    const entryCount = {};

// Loop through the entries array
    entries.forEach(entry => {
        // Create a unique key based on the properties of the entry
        const key = JSON.stringify(entry);

        // If the key (entry) exists, increment its count; otherwise, initialize its count to 1
        if (entryCount[key]) {
            entryCount[key].count++;
        } else {
            entryCount[key] = {...entry, count: 1};
        }
    });

    return Object.values(entryCount);
}

function numberElements(uniqueRows) {
    let counts = new Array(uniqueRows.length).fill(0);
    for (let i = 0; i < uniqueRows.length; i++) {
        let name = JSON.stringify(uniqueRows[i]);

        if (name.includes('Wandelement')) {
            uniqueRows[i] = [numberComponent(0, counts[0]++), ...uniqueRows[i]];
        } else if (name.includes('Rähm')) {
            uniqueRows[i] = [numberComponent(1, counts[1]++), ...uniqueRows[i]];
        } else if (name.includes('Schwelle')) {
            uniqueRows[i] = [numberComponent(2, counts[2]++), ...uniqueRows[i]];
        } else if (name.includes('Stütze')) {
            uniqueRows[i] = [numberComponent(3, counts[3]++), ...uniqueRows[i]];
        } else if (name.includes('Unterzug')) {
            uniqueRows[i] = [numberComponent(4, counts[4]++), ...uniqueRows[i]];
        } else if (name.includes('Brüstungsriegel')) {
            uniqueRows[i] = [numberComponent(5, counts[5]++), ...uniqueRows[i]];
        } else if (name.includes('Sturzriegel')) {
            uniqueRows[i] = [numberComponent(6, counts[6]++), ...uniqueRows[i]];
        } else if (name.includes('Deckenelement')) {
            uniqueRows[i] = [numberComponent(7, counts[7]++), ...uniqueRows[i]];
        } else if (name.includes('Attika')) {
            uniqueRows[i] = [numberComponent(8, counts[8]++), ...uniqueRows[i]];
        } else if (name.includes('Bodenelement')) {
            uniqueRows[i] = [numberComponent(9, counts[9]++), ...uniqueRows[i]];
        }
    }
}

const partNameKeys = {
    Wandelement: "part_wall",
    "Rähm": "part_top_plate",
    Schwelle: "part_bottom_plate",
    "Stütze": "part_post",
    Unterzug: "part_beam",
    "Brüstungsriegel": "part_sill",
    Sturzriegel: "part_lintel",
    Deckenelement: "part_ceiling",
    Attika: "part_parapet",
    Bodenelement: "part_floor",
};

function translatePartNames(rows) {
    rows.forEach((row) => {
        const key = partNameKeys[row[1]];
        if (key) {
            row[1] = t(key);
        }
    });
}

function numberComponent(base, count) {
    return base + ('000' + count).substr(-3);
}

function exportCSV(array, download) {
    var universalBOM = "\uFEFF";

    if (download) {
        let data = getCSVString(array);
        let encodedUri = 'data:text/csv;charset=utf-8,' + encodeURIComponent(universalBOM + data);
        let link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", t("parts_list_file"));
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    } else {
        //Send file to server
        if (templateId === "new") {
            console.error("Stückliste konnte nicht gespeichert werden.");
            return;
        }

        let data = getCSVString(array);
        const blob = new Blob([universalBOM + data], {type: 'text/csv'});
        const formData = new FormData();
        formData.append('csvFile', blob, 'data.csv');
        for (var i = 0; i < array.length; i++) {
          formData.append('elements[]', array[i]);
        }
        return saveCSVToServer(formData);
    }
}

function getCSVString(array) {
    let data = 'BtNr;BtName;Breite;Hoehe;Laenge;Volumen;Stueck;Summe Volumen\r\n';
    let totalCount = 0;
    let totalVolume = 0;

    array.forEach((element) => {
        totalCount += parseFloat(element[6]);
        totalVolume += parseFloat(element[7]);

        element = element.map(param => '"' + param.replace(".", ",") + '"');
        let row = element.join(";");
        data += row + "\r\n";

    });

    data += ';;;;;;' + totalCount.toFixed(0) + ';' + totalVolume.toFixed(3) + '\r\n';

    return data;
}

function saveCSVToServer(formData) {
    // Get the CSRF token value from the page (assuming it's stored in a <meta> tag)
    let csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute('content');

    formData.append('projectId', templateId);

    return fetch(saveCSVUrl, {
        method: 'POST',
        body: formData,
        async: false,
        headers: {
            "X-CSRF-TOKEN": csrfToken // Include the CSRF token in the header
        }
    })
        .then(response => {
            if (response.ok) {
                return response.json();
            } else {
                throw new Error('Failed to upload CSV file');
            }
        })
        .catch(error => {
            console.error('Error:', error);
        });
}

function getMillimeterMeasurement(input) {
    return Number(input ?? 0).toFixed(3);
    // return (parseFloat(input) * 100).toFixed(1);
}

function getRoundedVolume(volume) {
    return Number(volume ?? 0).toFixed(3);
    // return Math.round((volume + Number.EPSILON) * 1000) / 1000;
}

function hasFixedSizeUserData(element) {
    const {depth, height, width, originalVolume} = element.userData;

    return depth != null && height != null && width != null && originalVolume != null;
}
